import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Avatar } from 'src/components/Avatar';
import { CircularProgress } from 'src/components/CircularProgress';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { searchClients, searchUserClients } from 'src/models/user/user';

import type { ClientSearchResult } from 'src/models/user/_types';

interface ClientAutocompleteProps {
  value?: ClientSearchResult | ClientSearchResult[] | null;
  onChange: (client: ClientSearchResult | ClientSearchResult[] | null) => void;
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  filterOptions?: (options: ClientSearchResult[]) => ClientSearchResult[];
  filterSelectedOptions?: boolean;
  multiple?: boolean;
  searchTypes?: string[];
  placeholder?: string;
  secondary?: (option: ClientSearchResult) => string;
  includeUserSearch?: boolean;
  searchBy?: 'email' | 'phone_number' | 'name';
  trainersOnly?: boolean;
}

export const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  value,
  onChange,
  label,
  autoFocus,
  disabled,
  filterOptions,
  filterSelectedOptions,
  multiple,
  searchTypes = ['clients'],
  trainersOnly = false,
  placeholder,
  secondary,
  includeUserSearch = false,
  searchBy,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'clientAutocomplete' });
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<ClientSearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          '.MuiAutocomplete-input'
        ) as HTMLElement;
        input?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const {
    data: clients,
    refetch: refetchClients,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ['users', 'kiosk_search', searchValue, trainersOnly],
    queryFn: ({ signal }) =>
      includeUserSearch
        ? searchUserClients({
            search: searchValue,
            types: searchTypes,
            searchBy,
            signal,
          })
        : searchClients({
            search: searchValue,
            types: searchTypes,
            trainersOnly,
            signal,
          }),
    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  useEffect(() => {
    if (clients) {
      setOptions(clients);
    }
  }, [clients]);

  const debouncedSearch = debounce((value: string) => {
    if (value.length >= 2) {
      refetchClients();
    }
  }, 300);

  const handleInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    if (reason === 'input') {
      setSearchValue(value);
      debouncedSearch(value);
    }
  };

  return (
    <div ref={containerRef}>
      <Autocomplete
        disabled={disabled}
        filterSelectedOptions={filterSelectedOptions}
        multiple={multiple}
        options={options}
        getOptionLabel={(option: ClientSearchResult) => `${option.name}`}
        value={value || (multiple ? [] : null)}
        onChange={(_, newValue) => {
          onChange(multiple ? (newValue as ClientSearchResult[]) : newValue);
        }}
        onInputChange={handleInputChange}
        loading={searchFetching}
        filterOptions={(x) => (filterOptions ? filterOptions(x) : x)}
        noOptionsText={
          searchValue ? t('noAccountsFound') : t('startTypingToFindAccounts')
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? t('selectClient')}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchFetching ? (
                    <CircularProgress color="inherit" size="small" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <ListItem
            {...(props as any)}
            key={option.id}
            clickable
            primary={option.name}
            secondary={secondary ? secondary(option) : option.email}
            avatar={<Avatar src={option.profileImage} />}
          />
        )}
      />
    </div>
  );
};
